<template>
  <h1 class="sr-only">{{ special ? special.title : '스페셜' }}</h1>
  <!-- s::PAGE -->
  <div v-if="loading" class="loading"></div>

  <div v-else-if="error" class="error">{{ error.message }}</div>

  <div v-else class="contents">
    <!-- s:: ARTICLE -->
    <div class="article">
      <div class="layout">
        <div class="box">
          <ArticleSnb/>
          <div v-if="special" class="content-panel">
            <div class="special-view-wrap">
              <div class="special-view-title">
                <h3>{{ special.title }}
                  <span class="sub">{{ special.subTitle }}</span>
                </h3>
                <ArticleButtons v-model:isLike="special.isLike" :uuid="special.uuid" :likeType="special.likeType"/>
              </div>
              <div class="special-view-date">
                <span>{{ formatDateToYYYYMMDD(special.publishedAt) }}</span>
              </div>
            </div>

            <div class="view-type3">
              <figure v-if="special.mainContentUrl && special.mainContentType === 'IMAGE'">
                <img :src="special.mainContentUrl" :alt="special.title || '스페셜 대표 이미지'"/>
              </figure>
              <div v-else-if="special.mainContentUrl && special.mainContentType === 'VIDEO'" class="video-box">
                <iframe :src="getYoutubeEmbedUrl(special.mainContentUrl)" frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allowfullscreen loading="lazy"
                        :title="`${special.title} video`">
                </iframe>
              </div>
              <!-- task 007: 워드형 단일 본문(content_html) 우선, 없으면 기존 섹션 fallback -->
              <MimioContent v-if="special.contentHtml" :html="special.contentHtml"/>
              <template v-else>
                <div v-if="special.mainContent" class="txt-box white-space-pre-line"
                     v-safe-html="special.mainContent">
                </div>
                <template v-for="(content, index) in special.contents" :key="content.uuid">
                  <div :class="['box2', { reverse: index % 2 === 1 }]">
                    <div v-if="content.contentUrl" class="img">
                      <img :src="content.contentUrl" :alt="content.title"/>
                    </div>
                    <div class="txt">
                      <h3 v-if="content.title">{{ content.title }}</h3>
                      <p v-if="content.content" class="txt-box white-space-pre-line"
                         v-safe-html="content.content">
                      </p>
                    </div>
                  </div>
                </template>
              </template>
              <!-- relatedContents 렌더링 (새로운 방식) -->
              <div v-if="special.relatedContents && special.relatedContents.length" class="view-btn-wrap related-contents-wrap">
                <template v-for="(content, idx) in special.relatedContents" :key="idx">
                  <router-link v-if="isInternalPath(content.contentUrl)"
                               :to="parseInternalPath(content.contentUrl)"
                               style="width: 200px;">
                    <span>{{ content.buttonLabel }}</span>
                    <img src="@/assets/images/icon/ico_right.svg" alt="">
                  </router-link>
                  <a v-else :href="content.contentUrl" target="_blank" rel="noopener noreferrer"
                     style="display: flex; align-items: center; gap: 8px; text-decoration: none; color: inherit; width: 200px;">
                    <span>{{ content.buttonLabel }}</span>
                    <img src="@/assets/images/icon/ico_right.svg" alt="">
                  </a>
                </template>
              </div>

              <!-- 하단 white divider -->
              <!-- <div class="white-divider"></div> -->
              <div class="view-writer">
                <div class="writer-wrap">
                  <div class="writer-img-box">
                    <figure><img v-if="special.writerImageUrl" :src="special.writerImageUrl" alt="writer image"/></figure>
                  </div>
                  <div class="writer-txt-box">
                    <div class="writer-title">
                      <span>{{getWriterType(special.articleSpecialType)}}</span>
                      <span class="writer">{{ special.writer }}</span>
                    </div>
                    <p class="white-space-pre-line"
                       v-safe-html="special.writerMessage">
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- e:: ARTICLE -->
  </div>
  <!-- e::PAGE -->
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {apiClient} from "@/services/auth-header";
import ArticleSnb from "@/components/snb/ArticleSnb.vue";
import ArticleButtons from "@/components/article/ArticleButtons.vue";
import MimioContent from "@/components/common/MimioContent.vue";
import {useUiStore} from "@/store/useUiStore";
import { useAnalytics } from "@/composables/useAnalytics";
import { setSeoMeta, excerpt, DETAIL_FALLBACK_DESCRIPTION } from "@/composables/useSeoMeta";

const uiStore = useUiStore();
const { trackArticleView } = useAnalytics();

const router = useRouter();
const route = useRoute();
const articleSubTitle = ref(route.params.subTitle || false);
const articleUuid = ref(route.params.uuid);
const loading = ref(true);
const error = ref(null);
const special = ref(null);

const tempBtns = articleUuid.value === '910407189536456ba88139e7fde830db';

// YouTube URL을 embed 형식으로 변환
const getYoutubeEmbedUrl = (url) => {
  if (!url) return '';
  // YouTube URL 패턴: https://www.youtube.com/watch?v=xxxxx or https://youtu.be/xxxxx
  const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

// relatedContents URL 처리 유틸 함수
const isInternalPath = (url) => {
  return url && url.startsWith('/');
};

const parseInternalPath = (url) => {
  // "/artist/hyunminjasu" 형식 처리
  if (url.startsWith('/artist/')) {
    const userAccountId = url.replace('/artist/', '');
    return { name: 'ArtistDetail', params: { userAccountId } };
  }
  // 다른 내부 경로 추가 가능
  return null;
};

const getWriterType = (articleSpecialType) => {
  if (articleSpecialType === 'VIDEO') {
    return 'Executive Producer';
  } else {
    return 'Writer';
  }
};

const formatDateToYYYYMMDD = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0].replace(/-/g, '.');
};

// const formattedDate = computed(() => {
//   if (!special.value || !special.value.createdAt) {
//     return "";
//   }
//   const date = new Date(special.value.createdAt);
//   const year = date.getFullYear();
//   const month = String(date.getMonth() + 1).padStart(2, "0");
//   const day = String(date.getDate()).padStart(2, "0");
//   const hours = String(date.getHours()).padStart(2, "0");
//   const minutes = String(date.getMinutes()).padStart(2, "0");

//   // 'YYYY-MM-DD HH:mm' 형식으로 반환
//   return `${year}-${month}-${day} ${hours}:${minutes}`;
// });

/**
 * og:image 로 쓸 대표 이미지. 대표 콘텐츠가 동영상일 수 있으므로 타입을 본다 —
 * 동영상 URL 을 og:image 로 내보내면 공유 카드가 통째로 비어서 나간다.
 * 백엔드 SeoRenderService.specialImage() 와 같은 규칙이어야 한다 (봇 == 사람).
 */
const ogImageOf = (data) => {
  if (data.mainContentType !== 'VIDEO' && data.mainContentUrl) return data.mainContentUrl;
  if (data.previewContentType !== 'VIDEO' && data.previewContentUrl)
    return data.previewContentUrl;
  return undefined; // setSeoMeta 가 DEFAULT_IMAGE 로 떨어뜨린다
};

const fetchItem = async () => {
  let uuid = articleUuid.value;
  if (articleSubTitle.value) {
    uuid = articleUuid.value + '/' + decodeURIComponent(articleSubTitle.value);
  }
  try {
    uiStore.showLoading();
    const response = await apiClient.get('/v1/articles/specials/' + uuid);
    special.value = response.data.data;

    // 페이지별 SEO 메타 (mimio#59)
    // 스페셜은 TipTap 산출물이라 contentHtml 에 태그가 섞여 있다 — excerpt 가 벗겨낸다.
    setSeoMeta({
      title: `${special.value.title} | mimio`,
      description:
        excerpt(special.value.subTitle) ||
        excerpt(special.value.contentHtml) ||
        DETAIL_FALLBACK_DESCRIPTION.special,
      image: ogImageOf(special.value),
      type: "article",
    });
    trackArticleView({ ...special.value, id: special.value.uuid }, "special");
  } catch (error) {
    error.value = error; // 에러를 저장합니다.
    await router.replace({name: "Special"});
  } finally {
    loading.value = false; // 작업이 끝난 후에는 로딩 상태를 끝냅니다.
    uiStore.hideLoading();
  }
}

onMounted(async () => {
  await fetchItem();
});
</script>

<style scoped>
.temp-btn-wrap,
.related-contents-wrap {
  gap: 10px;
}

/* Video box - responsive styling for YouTube embeds */
.video-box {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  border-radius: 8px;
  background: #000;
  margin-bottom: 50px;
}

.video-box iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

@media (max-width: 768px) {
  .chip {
    display: block;
    margin-bottom: 8px;
  }

  .temp-btn-wrap,
  .related-contents-wrap {
    gap: 20px;
    flex-direction: column;
    align-items: center;
  }

  .video-box {
    margin-bottom: 30px;
  }
}

@media (max-width: 480px) {
  .video-box {
    margin-bottom: 20px;
  }
}
</style>